import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import {
  CheckCircle2,
  Globe2,
  Smartphone,
  Palette,
  Cpu,
  Cloud,
  ShieldCheck,
  Gauge,
  Layers,
  Users2,
  LifeBuoy,
  ArrowRight,
  ArrowUpRight,
} from "lucide-react";
import {
  RevealSection,
  SplitText,
  MagneticButton,
  HorizontalScrollSection, // new
  staggerContainer,
  staggerItem,
  useParallax,
  EASE,
} from "../components/motion/ScrollFX";

// ================= THEME =================
// Deep maroon + crimson, pulled from the UnibComp logo. Red only ever
// appears as a deliberate accent (text, icons, buttons) — never as a
// flat background tint. Surfaces are warm off-white rather than pure
// white, and the page alternates light / dark bands (hero, services,
// features, portfolio, cta) so it never reads as one flat white sheet.
const theme = {
  ink: "#221619",
  inkMuted: "#6B6265",
  accent: "#E63946",
  accentDeep: "#B22234",
  gold: "#D9A441",
  border: "#E6DFDC",
  surface: "#FBF8F6",
  surfaceAlt: "#F2ECE9",
  heroBg: "#1A0E10",
  darkBand: "#221619",
  darkBandDeep: "#160B0D",
  ctaBg: "#2B1013",
  ctaBgDeep: "#160A0C",
};

const process = [
  { no: "01", title: "Discover", desc: "We map your goals, users and technical constraints before writing a line of code." },
  { no: "02", title: "Design", desc: "Wireframes and UI direction, validated with you before development starts." },
  { no: "03", title: "Develop", desc: "Clean, modular builds on the stack that fits — web, mobile or custom software." },
  { no: "04", title: "Deploy", desc: "Tested, optimised and shipped with CI/CD and cloud infrastructure in place." },
  { no: "05", title: "Support", desc: "Ongoing monitoring and maintenance so the product keeps performing post-launch." },
];

// ================= DATA =================
// Hero copy is a fixed frame ("We build ___ digital solutions") with a
// single word rolling through the blank.
const heroWords = ["Powerful", "Scalable", "Secure", "Modern", "Reliable"];
const heroEyebrow = "Digital Product Studio";
const heroSubtitle =
  "Web, mobile, and custom software engineered for your business — from first sketch to launch.";

const services = [
  {
    no: "01",
    icon: Globe2,
    title: "Web Development",
    desc: "High-performance, SEO-friendly and scalable websites built with modern frameworks.",
  },
  {
    no: "02",
    icon: Smartphone,
    title: "Mobile App Development",
    desc: "Android & iOS applications focused on speed, security and user experience.",
  },
  {
    no: "03",
    icon: Palette,
    title: "UI / UX Design",
    desc: "Clean, intuitive and conversion-focused designs for real users.",
  },
  {
    no: "04",
    icon: Cpu,
    title: "Custom Software",
    desc: "Tailor-made enterprise solutions that fit your exact business workflow.",
  },
  {
    no: "05",
    icon: Cloud,
    title: "Cloud & DevOps",
    desc: "Scalable cloud infrastructure with monitoring, CI/CD and security.",
  },
];

// First feature is the "highlight" — it spans wider than the rest so
// the grid isn't six identical boxes.
const features = [
  {
    icon: Layers,
    title: "Scalable Systems",
    desc: "Our solutions grow with your business without compromising performance — architected to handle 10x traffic without a rewrite.",
    highlight: true,
  },
  {
    icon: ShieldCheck,
    title: "Enterprise Security",
    desc: "Security-first approach with encryption, monitoring and best practices.",
  },
  {
    icon: Gauge,
    title: "High Performance",
    desc: "Optimized applications delivering fast load times and a smooth experience.",
  },
  {
    icon: Cpu,
    title: "Modern Tech Stack",
    desc: "Built using React, Node, Cloud and the latest industry tools.",
  },
  {
    icon: Users2,
    title: "Client-Centric Approach",
    desc: "We listen, adapt and deliver solutions that meet real business needs.",
  },
  {
    icon: LifeBuoy,
    title: "Ongoing Support",
    desc: "Dedicated support & maintenance for long-term product success.",
  },
];

// "tall" / "short" controls the staggered portfolio rhythm.
const portfolio = [
  {
    title: "E-Commerce Website",
    category: "Web Development",
    img: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=900&auto=format&fit=crop",
    size: "tall",
  },
  {
    title: "Mobile Banking App",
    category: "Mobile App",
    img: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=900&auto=format&fit=crop",
    size: "short",
  },
  {
    title: "Startup Landing Page",
    category: "UI / UX Design",
    img: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=900&auto=format&fit=crop",
    size: "short",
  },
  {
    title: "Custom CRM Software",
    category: "Custom Software",
    img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=900&auto=format&fit=crop",
    size: "tall",
  },
  {
    title: "Cloud Dashboard",
    category: "Cloud Solutions",
    img: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=900&auto=format&fit=crop",
    size: "tall",
  },
  {
    title: "E-Learning Platform",
    category: "Web Development",
    img: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=900&auto=format&fit=crop",
    size: "short",
  },
];

// Nodes for the hero's constellation field — an abstract network reads
// as "connected systems" without borrowing dashboard or IDE chrome.
const networkNodes = [
  { x: 8, y: 18 }, { x: 34, y: 8 }, { x: 58, y: 22 }, { x: 82, y: 10 },
  { x: 20, y: 46 }, { x: 50, y: 52 }, { x: 76, y: 44 },
  { x: 12, y: 78 }, { x: 42, y: 84 }, { x: 68, y: 76 }, { x: 90, y: 66 },
];
const networkEdges = [
  [0, 1], [1, 2], [2, 3], [1, 4], [2, 5], [3, 6], [4, 5], [5, 6],
  [4, 7], [5, 8], [6, 9], [8, 9], [9, 10], [7, 8],
];

// Each service's spotlight panel pairs an icon with a distinct accent
// so switching between them feels like a real change, not a reskin.
const serviceAccents = ["#E63946", "#D9A441", "#E63946", "#D9A441", "#E63946"];

// Marquee content for the trust band under the hero — pulled from our
// own services rather than invented client logos.
const marqueeItems = [
  "Web Development",
  "Mobile Apps",
  "UI / UX Design",
  "Custom Software",
  "Cloud & DevOps",
  "Enterprise Support",
];

// Reusable section eyebrow.
function Eyebrow({ children, center = false, light = false }) {
  return (
    <div
      className={`inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase mb-4 ${
        center ? "mx-auto" : ""
      }`}
      style={{ color: light ? "#FF8A93" : theme.accent }}
    >
      <span className="h-px w-6" style={{ backgroundColor: light ? "#FF8A93" : theme.accent }} />
      {children}
    </div>
  );
}

function DotGrid({ className = "", light = false }) {
  return (
    <div
      className={`pointer-events-none absolute inset-0 ${light ? "opacity-[0.05]" : "opacity-[0.06]"} ${className}`}
      style={{
        backgroundImage: `radial-gradient(${light ? "#FFFFFF" : theme.ink} 1px, transparent 1px)`,
        backgroundSize: "26px 26px",
      }}
    />
  );
}

// ---------------------------------------------------------------------
// RPS-style word rotator: one line of fixed text, one word that rolls
// vertically in and out on a timer (mask + translateY).
//
// FIX: the rolling word is `position: absolute` (so it can slide in/out
// without pushing layout), which means it can never contribute to the
// parent's width. The parent previously relied on a guessed `minWidth:
// 6ch`, so any word wider than that guess — "Reliable", "Modern" — got
// its last letter sliced off by `overflow: hidden`. Now the wrapper's
// width is sized off the longest word in the list (in ch units) plus a
// small buffer, and horizontal overflow is left visible as a safety net
// while only the vertical axis stays clipped for the roll effect.
// ---------------------------------------------------------------------
function RotatingWord({ words, interval = 2200 }) {
  const [index, setIndex] = useState(0);
  const maxChars = Math.max(...words.map((w) => w.length));

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % words.length);
    }, interval);
    return () => clearInterval(timer);
  }, [words.length, interval]);

  return (
    <span
      className="relative inline-block align-bottom"
      style={{
        height: "1.08em",
        width: `${maxChars + 1.5}ch`,
        overflowY: "hidden",
        overflowX: "visible",
      }}
    >
      <AnimatePresence mode="popLayout">
        <motion.span
          key={words[index]}
          initial={{ y: "60%", opacity: 0 }}
          animate={{ y: "0%", opacity: 1 }}
          exit={{ y: "-60%", opacity: 0 }}
          transition={{ duration: 0.55, ease: [0.65, 0, 0.35, 1] }}
          className="absolute left-0 top-0 whitespace-nowrap"
          style={{ color: theme.accent }}
        >
          {words[index]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

// ---------------------------------------------------------------------
// Infinite marquee band — duplicated content track scrolling on a plain
// CSS keyframe loop.
// ---------------------------------------------------------------------
function MarqueeBand() {
  return (
    <div
      className="relative py-8 overflow-hidden border-y"
      style={{ backgroundColor: theme.darkBandDeep, borderColor: "rgba(255,255,255,0.08)" }}
    >
      <style>{`
        @keyframes marqueeScroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .marquee-track {
          animation: marqueeScroll 22s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .marquee-track { animation: none; }
        }
      `}</style>
      <div className="flex w-max marquee-track">
        {[...marqueeItems, ...marqueeItems].map((item, i) => (
          <span
            key={i}
            className="px-10 text-lg font-bold whitespace-nowrap"
            style={{ color: "rgba(255,255,255,0.35)" }}
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Hero background: an animated constellation — nodes drift slowly,
// edges pulse with traveling light like signal running through a
// network.
// ---------------------------------------------------------------------
function ConstellationField() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <span className="hero-mesh" />
      <span className="hero-glow-orbit" />
      <span className="hero-glow-orbit-gold" />
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        {networkEdges.map(([a, b], i) => {
          const n1 = networkNodes[a];
          const n2 = networkNodes[b];
          return (
            <line key={i} x1={n1.x} y1={n1.y} x2={n2.x} y2={n2.y} stroke="rgba(255,255,255,0.14)" strokeWidth="0.15" />
          );
        })}
        {networkEdges.map(([a, b], i) => {
          const n1 = networkNodes[a];
          const n2 = networkNodes[b];
          return (
            <line
              key={`pulse-${i}`}
              x1={n1.x}
              y1={n1.y}
              x2={n2.x}
              y2={n2.y}
              stroke={i % 2 === 0 ? "#E63946" : "#D9A441"}
              strokeWidth="0.3"
              strokeDasharray="2 14"
              className="network-pulse"
              style={{ animationDelay: `${i * 0.35}s` }}
            />
          );
        })}
        {networkNodes.map((n, i) => (
          <circle
            key={i}
            cx={n.x}
            cy={n.y}
            r={i % 3 === 0 ? 0.55 : 0.35}
            fill={i % 3 === 0 ? "#E63946" : "#FFFFFF"}
            className="network-node"
            style={{ animationDelay: `${i * 0.4}s` }}
          />
        ))}
      </svg>
      <span className="hero-dots" />
    </div>
  );
}

// ---------------------------------------------------------------------
// Hero signature: a tilted device stack (browser + phone) that reacts
// to the cursor with a subtle 3D parallax tilt.
// ---------------------------------------------------------------------
function DeviceStack() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 16 });

  const handleMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const handleLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className="relative w-full max-w-md mx-auto"
      style={{ perspective: 1200 }}
    >
      <div
        className="absolute -inset-10 rounded-[3rem] blur-3xl opacity-40"
        style={{ background: `radial-gradient(circle, ${theme.accent}, transparent 70%)` }}
      />

      <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="relative">
        {/* browser window */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="device-float relative rounded-2xl overflow-hidden shadow-2xl border border-white/10"
          style={{ backgroundColor: "#241417", transform: "translateZ(0px)" }}
        >
          <div className="flex items-center gap-1.5 px-4 py-3 border-b border-white/10">
            <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
            <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
            <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
            <span className="ml-3 text-[11px] text-white/40 font-mono">unibcomp.app</span>
          </div>
          <div className="p-6 pb-16">
            <div className="h-2 w-16 rounded-full mb-4" style={{ backgroundColor: theme.accent }} />
            <div className="h-3.5 w-4/5 rounded-full mb-2 bg-white/25" />
            <div className="h-3.5 w-3/5 rounded-full mb-4 bg-white/25" />
            <div className="h-2 w-full rounded-full mb-2 bg-white/20" />
            <div className="h-2 w-11/12 rounded-full mb-2 bg-white/20" />
            <div className="h-2 w-4/5 rounded-full mb-5 bg-white/20" />
            <div className="flex gap-3">
              <div className="h-8 w-24 rounded-md" style={{ backgroundColor: theme.accent }} />
              <div className="h-8 w-24 rounded-md border border-white/20" />
            </div>
            <div className="grid grid-cols-3 gap-3 mt-6">
              {[
                { icon: Gauge, value: "98", label: "Perf. score", tint: theme.accent },
                { icon: Users2, value: "40+", label: "Clients", tint: theme.gold },
                { icon: ShieldCheck, value: "24/7", label: "Support", tint: theme.accent },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="h-14 rounded-lg px-2.5 py-2 flex flex-col justify-between"
                  style={{ backgroundColor: "rgba(255,255,255,0.06)" }}
                >
                  <stat.icon size={13} style={{ color: stat.tint }} />
                  <div className="flex items-baseline justify-between">
                    <span className="text-white text-xs font-bold leading-none">{stat.value}</span>
                    <span className="text-white/35 text-[8px] leading-none">{stat.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* phone, layered on top */}
        <motion.div
          initial={{ opacity: 0, y: 40, x: -10 }}
          animate={{ opacity: 1, y: 0, x: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: "easeOut" }}
          className="device-float device-float-delayed absolute -bottom-8 -left-8 w-32 rounded-[1.4rem] overflow-hidden shadow-2xl border-4"
          style={{ backgroundColor: "#1C1013", borderColor: "#2E1A1D", transform: "translateZ(60px)" }}
        >
          <div className="h-3 flex items-center justify-center" style={{ backgroundColor: "#2E1A1D" }}>
            <span className="w-6 h-1 rounded-full bg-white/25" />
          </div>
          <div className="p-2.5">
            <div className="h-1.5 w-10 rounded-full mb-2" style={{ backgroundColor: theme.gold }} />
            <div className="space-y-1.5">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-6 rounded-md" style={{ backgroundColor: "rgba(255,255,255,0.07)" }} />
              ))}
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* floating status chip */}
      <motion.div
        initial={{ opacity: 0, x: 20, y: -10 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ delay: 0.9, duration: 0.6 }}
        className="absolute -right-4 top-8 rounded-xl px-4 py-3 shadow-xl border border-white/10 flex items-center gap-3"
        style={{ backgroundColor: "#2E1A1D" }}
      >
        <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: theme.accent }}>
          <Layers size={16} className="text-white" />
        </span>
        <div>
          <div className="text-white text-xs font-semibold">One codebase</div>
          <div className="text-white/40 text-[10px]">Web · iOS · Android</div>
        </div>
      </motion.div>
    </div>
  );
}

export default function Home() {
  const [activeService, setActiveService] = useState(0);
  const heroRef = useRef(null);
  const bgParallax = useParallax(heroRef, 50);
  const deviceParallax = useParallax(heroRef, -24);

  return (
    <div className="w-full" style={{ backgroundColor: theme.surface }}>
      {/* Keyframes for the moving hero background and card surface
         treatments. Kept in one place, respects reduced-motion. */}
      <style>{`
        /* ---- Hero: rotating ambient glow behind the constellation ---- */
        .hero-glow-orbit {
          position: absolute;
          width: 46rem;
          height: 46rem;
          top: 50%;
          left: 50%;
          margin: -23rem 0 0 -23rem;
          border-radius: 9999px;
          filter: blur(80px);
          opacity: 0.5;
          background: radial-gradient(circle, ${theme.accent}, transparent 65%);
          animation: glowOrbit 20s ease-in-out infinite;
        }
        @keyframes glowOrbit {
          0%   { transform: translate(-10rem, -4rem) scale(1); }
          33%  { transform: translate(9rem, -6rem) scale(1.15); }
          66%  { transform: translate(4rem, 7rem) scale(0.9); }
          100% { transform: translate(-10rem, -4rem) scale(1); }
        }

        /* ---- Hero: second, slower, opposite-path gold orb for depth ---- */
        .hero-glow-orbit-gold {
          position: absolute;
          width: 34rem;
          height: 34rem;
          top: 50%;
          left: 50%;
          margin: -17rem 0 0 -17rem;
          border-radius: 9999px;
          filter: blur(90px);
          opacity: 0.32;
          background: radial-gradient(circle, ${theme.gold}, transparent 65%);
          animation: glowOrbitGold 26s ease-in-out infinite;
        }
        @keyframes glowOrbitGold {
          0%   { transform: translate(8rem, 6rem) scale(1); }
          40%  { transform: translate(-9rem, 3rem) scale(0.85); }
          70%  { transform: translate(-3rem, -8rem) scale(1.1); }
          100% { transform: translate(8rem, 6rem) scale(1); }
        }

        /* ---- Hero: faint diagonal mesh, panning slowly for a "systems" feel ---- */
        .hero-mesh {
          position: absolute;
          inset: -20%;
          opacity: 0.05;
          background-image:
            linear-gradient(115deg, transparent 0%, transparent 48%, rgba(255,255,255,0.6) 49%, transparent 51%, transparent 100%),
            linear-gradient(25deg, transparent 0%, transparent 48%, rgba(230,57,70,0.5) 49%, transparent 51%, transparent 100%);
          background-size: 140px 140px, 220px 220px;
          animation: meshPan 50s linear infinite;
        }
        @keyframes meshPan {
          from { background-position: 0 0, 0 0; }
          to { background-position: 560px 280px, -440px 220px; }
        }

        /* ---- Hero: constellation nodes drifting + signal pulses ---- */
        .network-node {
          animation: nodeDrift 6s ease-in-out infinite;
        }
        @keyframes nodeDrift {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(0, -1.4px); }
        }
        .network-pulse {
          animation: pulseFlow 3.5s linear infinite;
        }
        @keyframes pulseFlow {
          from { stroke-dashoffset: 32; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          to { stroke-dashoffset: 0; opacity: 0; }
        }

        /* ---- Hero: gently panning dot texture ---- */
        .hero-dots {
          position: absolute;
          inset: -10px;
          opacity: 0.06;
          background-image: radial-gradient(#FFFFFF 1px, transparent 1px);
          background-size: 28px 28px;
          animation: dotsDrift 40s linear infinite;
        }
        @keyframes dotsDrift {
          from { background-position: 0 0; }
          to { background-position: 280px 280px; }
        }

        /* ---- Hero: device stack idle float ---- */
        .device-float {
          animation: deviceFloatBrowser 5s ease-in-out infinite;
        }
        .device-float-delayed {
          animation: deviceFloatPhone 5s ease-in-out infinite;
          animation-delay: 0.6s;
        }
        @keyframes deviceFloatBrowser {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes deviceFloatPhone {
          0%, 100% { transform: translateY(0) translateZ(60px); }
          50% { transform: translateY(-10px) translateZ(60px); }
        }

        /* ---- Card surface treatments ---- */
        .card-glow {
          pointer-events: none;
          position: absolute;
          top: -3rem;
          right: -3rem;
          width: 9rem;
          height: 9rem;
          border-radius: 9999px;
          filter: blur(30px);
          background: ${theme.accent};
          opacity: 0.08;
          transition: opacity 0.3s ease;
        }
        .group:hover .card-glow { opacity: 0.16; }
        .card-glow-dark {
          pointer-events: none;
          position: absolute;
          inset: 0;
          background:
            radial-gradient(circle at 15% 15%, rgba(230,57,70,0.22), transparent 55%),
            repeating-linear-gradient(45deg, rgba(255,255,255,0.035) 0px, rgba(255,255,255,0.035) 2px, transparent 2px, transparent 20px);
        }

        /* ---- Portfolio: image hover overlay ---- */
        .portfolio-overlay {
          opacity: 0;
          transform: translateY(8px);
          transition: opacity 0.35s ease, transform 0.35s ease;
        }
        .group:hover .portfolio-overlay {
          opacity: 1;
          transform: translateY(0);
        }

        /* ---- Portfolio: diagonal light "mask" sweeping across the image
           on hover — reads as a premium editorial shine wipe rather than
           a plain zoom. ---- */
        .img-mask {
          position: relative;
        }
        .img-mask::after {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(115deg, transparent 35%, rgba(255,255,255,0.35) 50%, transparent 65%);
          transform: translateX(-140%) skewX(-12deg);
          transition: transform 0.75s cubic-bezier(0.22, 1, 0.36, 1);
          pointer-events: none;
        }
        .group:hover .img-mask::after {
          transform: translateX(140%) skewX(-12deg);
        }

        @media (prefers-reduced-motion: reduce) {
          .hero-glow-orbit, .hero-glow-orbit-gold, .hero-mesh, .network-node,
          .network-pulse, .hero-dots, .device-float, .device-float-delayed { animation: none !important; }
        }
      `}</style>

      {/* ================= HERO (dark) ================= */}
      <section
        ref={heroRef}
        className="relative overflow-hidden pt-40 pb-24 md:pt-48 md:pb-32"
        style={{ backgroundColor: theme.heroBg }}
      >
        <motion.div style={{ y: bgParallax }} className="absolute inset-0">
          <ConstellationField />
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <Eyebrow light>{heroEyebrow}</Eyebrow>
            <h1 className="text-4xl md:text-[3.4rem] font-extrabold leading-[1.08] tracking-tight text-white">
              <SplitText text="We build" className="text-white" />{" "}
              <RotatingWord words={heroWords} />
              <br />
              <SplitText text="digital solutions" className="text-white" delay={0.25} />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55, ease: EASE }}
              className="mt-6 text-lg text-white/60 max-w-lg"
            >
              {heroSubtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7, ease: EASE }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <MagneticButton>
                <Link
                  to="/contact"
                  data-cursor="hover"
                  style={{ backgroundColor: theme.accent }}
                  className="group inline-flex items-center gap-2 text-white px-7 py-3.5 rounded-md font-semibold hover:brightness-110 transition-all"
                >
                  Get Started
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </MagneticButton>
              <MagneticButton strength={0.25}>
                <Link
                  to="/services"
                  data-cursor="hover"
                  className="inline-flex items-center gap-2 border border-white/25 text-white px-7 py-3.5 rounded-md font-medium hover:bg-white hover:text-[#221619] transition-colors"
                >
                  Our Services
                </Link>
              </MagneticButton>
            </motion.div>
          </motion.div>

          <motion.div style={{ y: deviceParallax }}>
            <DeviceStack />
          </motion.div>
        </div>
      </section>

      {/* ================= MARQUEE (dark, scrolling capability strip) ================= */}
      <MarqueeBand />

      {/* ================= SERVICES (dark band, interactive spotlight) ================= */}
      <section className="relative py-24 overflow-hidden" style={{ backgroundColor: theme.darkBand }}>
        <DotGrid light />
        <div
          className="pointer-events-none absolute top-0 right-0 w-[32rem] h-[32rem] rounded-full blur-3xl opacity-[0.12] translate-x-1/3 -translate-y-1/3 transition-colors duration-500"
          style={{ backgroundColor: serviceAccents[activeService] }}
        />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <RevealSection className="max-w-2xl mb-16">
            <Eyebrow light>What We Do</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-extrabold leading-tight text-white">
              <SplitText text="Services that help your" />{" "}
              <SplitText text="business grow faster" delay={0.2} className="inline" as="span" />
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-white/60">
              At UniBcomp, we design, develop and scale digital products that
              deliver real business value.
            </p>
          </RevealSection>

          <RevealSection
            delay={0.1}
            className="grid md:grid-cols-[280px_1fr] gap-4 md:gap-0 rounded-3xl border border-white/10 overflow-hidden"
          >
            {/* tab list */}
            <div className="flex md:flex-col overflow-x-auto md:overflow-visible" style={{ backgroundColor: "rgba(255,255,255,0.03)" }}>
              {services.map((item, i) => {
                const isActive = i === activeService;
                return (
                  <button
                    key={item.no}
                    data-cursor="hover"
                    onClick={() => setActiveService(i)}
                    onMouseEnter={() => setActiveService(i)}
                    className="relative flex items-center gap-3 text-left px-6 py-5 shrink-0 w-full transition-colors duration-300"
                    style={{
                      backgroundColor: isActive ? "rgba(255,255,255,0.05)" : "transparent",
                    }}
                  >
                    <span
                      className="absolute left-0 top-0 bottom-0 w-[3px] transition-transform duration-300 origin-left"
                      style={{
                        backgroundColor: serviceAccents[i],
                        transform: isActive ? "scaleX(1)" : "scaleX(0)",
                      }}
                    />
                    <span
                      className="font-mono text-xs font-semibold transition-colors duration-300"
                      style={{ color: isActive ? serviceAccents[i] : "rgba(255,255,255,0.35)" }}
                    >
                      {item.no}
                    </span>
                    <span
                      className="text-sm font-semibold whitespace-nowrap transition-colors duration-300"
                      style={{ color: isActive ? "#FFFFFF" : "rgba(255,255,255,0.55)" }}
                    >
                      {item.title}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* spotlight panel */}
            <div className="relative p-10 md:p-14 min-h-[280px] flex items-center" style={{ backgroundColor: "rgba(255,255,255,0.02)" }}>
              <span className="pointer-events-none absolute -right-10 -bottom-10 opacity-[0.06]" style={{ color: "#FFFFFF" }}>
                {(() => {
                  const ActiveIcon = services[activeService].icon;
                  return <ActiveIcon size={220} strokeWidth={1} />;
                })()}
              </span>

              <AnimatePresence mode="wait">
                <motion.div
                  key={activeService}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className="relative z-10 max-w-lg"
                >
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                    style={{ backgroundColor: `${serviceAccents[activeService]}26`, color: serviceAccents[activeService] }}
                  >
                    {(() => {
                      const ActiveIcon = services[activeService].icon;
                      return <ActiveIcon size={26} />;
                    })()}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white">
                    {services[activeService].title}
                  </h3>
                  <p className="mt-4 text-white/60 leading-relaxed">
                    {services[activeService].desc}
                  </p>
                  <Link
                    to="/services"
                    data-cursor="hover"
                    style={{ color: serviceAccents[activeService] }}
                    className="mt-6 inline-flex items-center gap-2 font-semibold hover:brightness-110 transition-all w-fit"
                  >
                    Learn more
                    <ArrowUpRight size={16} />
                  </Link>
                </motion.div>
              </AnimatePresence>
            </div>
          </RevealSection>

          <RevealSection delay={0.2} className="mt-10 flex flex-wrap gap-x-10 gap-y-4">
            {[
              "Scalable & secure architecture",
              "Modern UI with smooth UX",
              "Long-term technical support",
            ].map((item) => (
              <div key={item} className="flex items-center gap-3 text-white/70">
                <CheckCircle2 size={18} style={{ color: theme.accent }} className="shrink-0" />
                <span className="text-sm">{item}</span>
              </div>
            ))}
          </RevealSection>
        </div>
      </section>

      {/* ================= FEATURES (bento, light) ================= */}
      <section className="relative py-24 overflow-hidden" style={{ backgroundColor: theme.surfaceAlt }}>
        <DotGrid />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <RevealSection className="text-center max-w-3xl mx-auto mb-16">
            <Eyebrow center>Why UniBcomp</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-extrabold" style={{ color: theme.ink }}>
              <SplitText text="Features that make" /> <span style={{ color: theme.accent }}>UniBcomp</span>{" "}
              <SplitText text="different" delay={0.15} />
            </h2>
            <p className="mt-4 text-lg" style={{ color: theme.inkMuted }}>
              Built with performance, security and scalability at the core.
            </p>
          </RevealSection>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-[1fr]"
          >
            {features.map((item, i) => (
              <motion.div
                variants={staggerItem}
                key={item.title}
                data-cursor="hover"
                whileHover={{ y: -6, scale: 1.025, rotate: i % 2 === 0 ? -0.6 : 0.6 }}
                transition={{ type: "spring", stiffness: 260, damping: 18 }}
                className={`group relative overflow-hidden rounded-3xl p-8 border shadow-sm hover:shadow-2xl flex flex-col ${
                  item.highlight ? "sm:col-span-2" : ""
                }`}
                style={{
                  backgroundColor: item.highlight ? theme.ink : "#FFFFFF",
                  borderColor: item.highlight ? theme.ink : theme.border,
                }}
              >
                {item.highlight ? <span className="card-glow-dark" /> : <span className="card-glow" />}

                {/* reveal panel — slides up from the bottom edge on hover */}
                <span
                  className="feature-reveal pointer-events-none absolute inset-x-0 bottom-0 h-1.5 group-hover:h-full transition-all duration-500 ease-out"
                  style={{ backgroundColor: item.highlight ? "rgba(230,57,70,0.14)" : theme.accent, opacity: item.highlight ? 1 : 0.08 }}
                />

                <div className={`relative ${item.highlight ? "flex items-start gap-6" : ""}`}>
                  <div
                    className="w-14 h-14 flex items-center justify-center rounded-xl mb-6 shrink-0 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6"
                    style={{
                      backgroundColor: item.highlight ? "rgba(230,57,70,0.18)" : theme.surfaceAlt,
                      color: item.highlight ? "#FF8A93" : theme.accent,
                    }}
                  >
                    <item.icon size={24} />
                  </div>
                  <div>
                    <h3
                      className="text-xl font-semibold transition-colors"
                      style={{ color: item.highlight ? "#FFFFFF" : theme.ink }}
                    >
                      {item.title}
                    </h3>
                    <p
                      className="mt-3 leading-relaxed"
                      style={{ color: item.highlight ? "rgba(255,255,255,0.65)" : theme.inkMuted }}
                    >
                      {item.desc}
                    </p>
                    <span
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
                      style={{ color: item.highlight ? "#FF8A93" : theme.accent }}
                    >
                      Learn more <ArrowUpRight size={14} />
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

{/* ================= PROCESS (horizontal pinned scroll, dark) ================= */}
<section className="relative py-24 md:py-0" style={{ backgroundColor: theme.darkBand }}>
  <div className="max-w-7xl mx-auto px-6 md:pt-24">
    <RevealSection className="max-w-2xl mb-16">
      <Eyebrow light>How We Work</Eyebrow>
      <h2 className="text-4xl md:text-5xl font-extrabold leading-tight text-white">
        <SplitText text="A process built for" /> <SplitText text="momentum" delay={0.2} />
      </h2>
    </RevealSection>
  </div>

  <div className="md:h-screen">
    <HorizontalScrollSection
      className="px-6 md:h-full md:flex md:items-center"
      items={process}
      accent={theme.accent}
      renderCard={(step) => (
        <div
          data-cursor="hover"
          className="group relative rounded-3xl border p-8 h-full min-h-[280px] flex flex-col justify-between overflow-hidden"
          style={{ backgroundColor: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.1)" }}
        >
          <span className="card-glow-dark" />
          <span className="relative font-mono text-sm font-semibold" style={{ color: theme.accent }}>
            {step.no}
          </span>
          <div className="relative">
            <h3 className="text-2xl font-bold text-white">{step.title}</h3>
            <p className="mt-3 text-white/60 leading-relaxed">{step.desc}</p>
          </div>
        </div>
      )}
    />
  </div>
</section>

      {/* ================= PORTFOLIO (staggered, light) ================= */}
      <section className="relative py-24" style={{ backgroundColor: theme.surface }}>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <RevealSection>
            <Eyebrow center>Selected Work</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-extrabold" style={{ color: theme.ink }}>
              <SplitText text="Our" /> <span style={{ color: theme.accent }}>Portfolio</span>
            </h2>
            <p className="mt-4 text-lg max-w-2xl mx-auto" style={{ color: theme.inkMuted }}>
              A showcase of some of our recent projects delivering scalable,
              secure and high-quality digital solutions.
            </p>
          </RevealSection>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="mt-16 grid sm:grid-cols-2 md:grid-cols-3 gap-6 text-left"
          >
            {portfolio.map((project, i) => (
              <motion.div
                variants={staggerItem}
                whileHover={{ y: -10, rotate: i % 2 === 0 ? -0.8 : 0.8, scale: 1.015 }}
                transition={{ type: "spring", stiffness: 240, damping: 18 }}
                key={project.title}
                data-cursor="hover"
                className={`group relative overflow-hidden rounded-2xl border shadow-sm hover:shadow-2xl ${
                  project.size === "tall" ? "md:mt-0" : "md:mt-10"
                }`}
                style={{ backgroundColor: theme.surface, borderColor: theme.border }}
              >
                <div className="img-mask relative overflow-hidden">
                  <img
                    src={project.img}
                    alt={project.title}
                    loading="lazy"
                    className={`w-full object-cover group-hover:scale-110 group-hover:rotate-1 transition-transform duration-500 ${
                      project.size === "tall" ? "h-72" : "h-48"
                    }`}
                    style={{ backgroundColor: theme.surfaceAlt }}
                  />
                  <div
                    className="absolute inset-0"
                    style={{ background: "linear-gradient(to top, rgba(34,22,25,0.75), transparent 55%)" }}
                  />
                  <div className="portfolio-overlay absolute bottom-4 left-5 right-5 flex items-center justify-between">
                    <span className="text-white text-sm font-semibold">View Project</span>
                    <span
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: theme.accent }}
                    >
                      <ArrowUpRight size={15} className="text-white" />
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <span className="text-xs font-semibold tracking-wide uppercase" style={{ color: theme.accent }}>
                    {project.category}
                  </span>
                  <h3 className="mt-1 text-lg font-semibold" style={{ color: theme.ink }}>
                    {project.title}
                  </h3>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= CTA (dark) ================= */}
      <section
        className="relative py-28 overflow-hidden"
        style={{ background: `linear-gradient(160deg, ${theme.ctaBg} 0%, ${theme.ctaBgDeep} 100%)` }}
      >
        <DotGrid light />

        <div
          className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] rounded-full blur-3xl opacity-[0.14]"
          style={{ backgroundColor: theme.accent }}
        />

        <span
          aria-hidden="true"
          className="pointer-events-none select-none absolute -left-6 -top-10 text-[14rem] font-extrabold leading-none"
          style={{ color: "rgba(255,255,255,0.04)" }}
        >
          {"</>"}
        </span>

        <RevealSection className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <Eyebrow center light>Let's Build Together</Eyebrow>

          <h2 className="text-4xl md:text-5xl font-extrabold leading-tight text-white">
            <SplitText text="Ready to build your" />
            <span className="block">
              <SplitText text="next big" delay={0.15} /> <span style={{ color: theme.accent }}>project?</span>
            </span>
          </h2>

          <p className="mt-6 text-lg text-white/60 max-w-xl mx-auto">
            Partner with UniBcomp to build secure, scalable and
            high-performance digital solutions.
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <MagneticButton>
              <Link
                to="/contact"
                data-cursor="hover"
                style={{ backgroundColor: theme.accent }}
                className="group inline-flex items-center gap-2 px-9 py-4 rounded-md text-white font-semibold text-lg shadow-lg shadow-black/30 hover:brightness-110 transition-all"
              >
                Contact Us Now
                <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </MagneticButton>

            <MagneticButton strength={0.25}>
              <Link
                to="/work"
                data-cursor="hover"
                className="inline-flex items-center gap-2 border border-white/20 text-white px-9 py-4 rounded-md font-medium text-lg hover:bg-white hover:text-[#221619] transition-colors"
              >
                View Our Work
              </Link>
            </MagneticButton>
          </div>
        </RevealSection>
      </section>
    </div>
  );
}